import { PDFDocument, StandardFonts, rgb, ParseSpeeds } from 'pdf-lib';

/** Parallel certificate downloads — avoids long sequential network waits */
const DEFAULT_FETCH_CONCURRENCY = 6;

async function prefetchCertificatePdfBuffers(selectedRows, getCertificateUrl, concurrency, onProgress) {
  const n = selectedRows.length;
  const buffers = new Array(n).fill(undefined);

  for (let start = 0; start < n; start += concurrency) {
    const sliceLen = Math.min(concurrency, n - start);
    await Promise.all(
      Array.from({ length: sliceLen }, (_, k) => {
        const idx = start + k;
        return (async () => {
          const row = selectedRows[idx];
          const url = await getCertificateUrl(row);
          if (!url) {
            buffers[idx] = null;
            onProgress?.({ phase: 'fetch', index: idx, total: n, status: 'no-url' });
            return;
          }
          try {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
            }
            const pdfBytes = await response.arrayBuffer();
            if (!pdfBytes || pdfBytes.byteLength === 0) {
              throw new Error('Empty PDF file received');
            }
            const uint8Array = new Uint8Array(pdfBytes.slice(0, 4));
            const header = String.fromCharCode(...uint8Array);
            if (!header.startsWith('%PDF')) {
              throw new Error(`Invalid PDF format. Header: ${header}`);
            }
            buffers[idx] = pdfBytes;
            onProgress?.({ phase: 'fetch', index: idx, total: n, status: 'ok' });
          } catch (err) {
            buffers[idx] = false;
            onProgress?.({ phase: 'fetch', index: idx, total: n, status: 'error', error: err });
          }
        })();
      })
    );
  }

  return buffers;
}

/**
 * Load certificate PDF with thorough parsing first — Fast parsing often merges as blank pages.
 */
async function loadPdfDocForMerge(pdfBytes) {
  const attempts = [
    {
      ignoreEncryption: true,
      updateMetadata: false,
      parseSpeed: ParseSpeeds.Slow,
      capNumbers: true,
    },
    {
      ignoreEncryption: true,
      updateMetadata: false,
      parseSpeed: ParseSpeeds.Medium,
      capNumbers: true,
    },
    {
      ignoreEncryption: true,
      parseSpeed: ParseSpeeds.Fast,
    },
  ];

  for (const opts of attempts) {
    try {
      return await PDFDocument.load(pdfBytes, opts);
    } catch {
      /* try next */
    }
  }
  return null;
}

/**
 * Rasterize a single page via pdfjs-dist and embed it into mergedPdf.
 * Returns true on success, false on failure.
 */
async function rasterizePage(mergedPdf, pdfBytes, pageNumber, pageWidth, pageHeight, renderPdfPageAsImage) {
  try {
    const imageDataUrl = await renderPdfPageAsImage(pdfBytes, pageNumber);
    if (!imageDataUrl) return false;

    const base64Data = imageDataUrl.split(',')[1];
    const imageBytes = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
    const image = await mergedPdf.embedPng(imageBytes);
    const newPage = mergedPdf.addPage([pageWidth, pageHeight]);
    const imageDims = image.scaleToFit(pageWidth, pageHeight);
    newPage.drawImage(image, {
      x: 0,
      y: 0,
      width: imageDims.width,
      height: imageDims.height,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Append all pages of a certificate PDF into the merged document.
 *
 * Strategy — raster-first (prevents blank pages reliably):
 *  1. Load via pdf-lib to get page metadata (count, dimensions).
 *  2. For each page, render via pdfjs-dist (handles all PDF structures).
 *  3. If raster fails for a page, fall back to pdf-lib vector copy.
 *
 * Why raster-first: pdf-lib's copyPages() can silently produce blank pages
 * when the source PDF has complex content streams, encrypted resources, or
 * font subsets that don't transfer correctly — there is no reliable way to
 * detect this without actually rendering. pdfjs-dist (Firefox's engine) renders
 * all PDFs reliably. The major performance optimization (parallel downloads)
 * is fully retained.
 */
async function appendCertificatePdfToMerged(mergedPdf, pdfBytes, font, renderPdfPageAsImage) {
  const pdfDoc = await loadPdfDocForMerge(pdfBytes);
  if (!pdfDoc) return false;

  const pageIndices = pdfDoc.getPageIndices();
  const pageCount = pageIndices.length;
  if (pageCount === 0) return false;

  for (let pageIdx = 0; pageIdx < pageCount; pageIdx++) {
    const sourcePageIndex = pageIndices[pageIdx];
    const sourcePage = pdfDoc.getPage(sourcePageIndex);
    const { width: pageWidth, height: pageHeight } = sourcePage.getSize();
    // pdfjs-dist uses 1-based page numbers
    const pdfJsPageNumber = sourcePageIndex + 1;

    let pageAdded = false;

    // Primary: Raster via pdfjs-dist — reliable for ALL PDF structures
    pageAdded = await rasterizePage(
      mergedPdf, pdfBytes, pdfJsPageNumber, pageWidth, pageHeight, renderPdfPageAsImage
    );

    // Fallback: Vector copy via pdf-lib (only if raster failed, which is rare)
    if (!pageAdded) {
      try {
        const [copiedPage] = await mergedPdf.copyPages(pdfDoc, [sourcePageIndex]);
        if (copiedPage) {
          mergedPdf.addPage(copiedPage);
          pageAdded = true;
        }
      } catch {
        /* vector copy also failed */
      }
    }

    if (!pageAdded) {
      console.warn(`[combinedPdfReport] Could not merge page ${pdfJsPageNumber} — skipped`);
    }
  }

  return true;
}

/**
 * Common utility function to generate combined PDF reports
 * @param {Object} config - Configuration object
 * @param {Array} config.selectedRows - Array of selected rows to include in the report
 * @param {Function} config.renderPdfPageAsImage - Function to render PDF pages as images (from pdfjs-dist)
 * @param {Function} config.getCertificateUrl - Async function that takes a row and returns certificate URL or null
 * @param {Object} config.reportConfig - Report configuration
 * @param {string} config.reportConfig.heading - Main heading text
 * @param {string} config.reportConfig.section1Title - Section 1 title
 * @param {string} config.reportConfig.section2Title - Section 2 title
 * @param {string} config.reportConfig.fileName - Download file name
 * @param {Object} config.tableConfig - Table configuration
 * @param {Array<string>} config.tableConfig.headers - Array of header strings
 * @param {Function} config.tableConfig.getColumnWidths - Function that takes (font, tableWidth) and returns column widths object
 * @param {Function} config.tableConfig.formatRowData - Function that takes (row, index) and returns array of cell values
 * @param {Object} config.tableConfig.styling - Optional table styling config
 * @param {number} config.tableConfig.styling.rowHeight - Row height (default: 25)
 * @param {number} config.tableConfig.styling.fontSize - Font size (default: 9)
 * @param {number} config.tableConfig.styling.headerFontSize - Header font size (default: 10)
 * @param {boolean} config.tableConfig.styling.enableTextWrapping - Enable text wrapping (default: false)
 * @param {Function} config.tableConfig.styling.wrapText - Optional wrapText function for text wrapping
 * @param {Function} config.onProgress - Optional callback for progress updates
 * @param {number} [config.fetchConcurrency=6] - Max parallel certificate downloads
 * @returns {Promise<Object>} - Returns { successCount, failCount, totalPages }
 */
export const generateCombinedPdfReport = async ({
  selectedRows,
  renderPdfPageAsImage,
  getCertificateUrl,
  reportConfig,
  tableConfig,
  onProgress = null,
  fetchConcurrency = DEFAULT_FETCH_CONCURRENCY,
}) => {
  const { heading, section1Title, section2Title, fileName } = reportConfig;
  const {
    headers,
    getColumnWidths,
    formatRowData,
    styling = {},
  } = tableConfig;

  const {
    rowHeight = 25,
    fontSize = 9,
    headerFontSize = 10,
    enableTextWrapping = false,
    wrapText = null,
  } = styling;

  // Initialize counters
  let successCount = 0;
  let failCount = 0;

  // Create PDF document
  const mergedPdf = await PDFDocument.create();
  const font = await mergedPdf.embedFont(StandardFonts.Helvetica);

  // Create Summary Page
  let page = mergedPdf.addPage([595, 842]); // A4
  const { width, height } = page.getSize();
  let y = height - 50;

  // === TOP ROW (Logo + Heading) ===
  const rowHeightHeader = 50;
  const logoWidth = width * 0.3;
  const headingWidth = width - logoWidth - 100;

  // Border for top row
  page.drawRectangle({
    x: 50,
    y: y - rowHeightHeader,
    width: logoWidth + headingWidth,
    height: rowHeightHeader,
    borderColor: rgb(0, 0, 0),
    borderWidth: 1,
  });

  // Divider line
  page.drawLine({
    start: { x: 50 + logoWidth, y: y - rowHeightHeader },
    end: { x: 50 + logoWidth, y },
    thickness: 1,
    color: rgb(0, 0, 0),
  });

  // === Logo ===
  const logoUrl = '/static/media/logoCrc.a3f0968065d2eab3cf8c.png';
  try {
    const logoImageBytes = await fetch(logoUrl).then((res) => res.arrayBuffer());
    const logoImage = await mergedPdf.embedPng(logoImageBytes);
    const logoDims = logoImage.scaleToFit(logoWidth - 10, rowHeightHeader - 10);
    const logoY = y - rowHeightHeader + (rowHeightHeader - logoDims.height) / 2;
    page.drawImage(logoImage, {
      x: 55,
      y: logoY,
      width: logoDims.width,
      height: logoDims.height,
    });
  } catch (logoError) {
    // Continue without logo if it fails to load
    console.warn('Failed to load logo:', logoError);
  }

  // === Heading ===
  const headingFontSize = 12;
  const textWidth = font.widthOfTextAtSize(heading, headingFontSize);
  const headingX = 50 + logoWidth + (headingWidth - textWidth) / 2;
  const headingY = y - rowHeightHeader + (rowHeightHeader - headingFontSize) / 2;
  page.drawText(heading, {
    x: headingX,
    y: headingY,
    size: headingFontSize,
    font,
  });

  y -= rowHeightHeader + 40;

  // === Section 1.0 ===
  page.drawText(section1Title, { x: 50, y, size: 14, font });
  y -= 10;

  // Gray underline
  page.drawLine({
    start: { x: 50, y: y },
    end: { x: width - 50, y: y },
    thickness: 2,
    color: rgb(0.7, 0.7, 0.7),
  });
  y -= 20;

  // === TABLE HEADERS ===
  const tableX = 50;
  const tableWidth = width - 100;
  const tableRowHeight = rowHeight;
  const padding = enableTextWrapping ? 20 : 16;

  // Get column widths - pass font, tableWidth, padding, and fontSize
  const colWidths = getColumnWidths(font, tableWidth, padding, fontSize);
  const headerCols = Object.values(colWidths);

  // Handle header text wrapping if enabled
  let wrappedHeaders = null;
  let headerRowHeight = tableRowHeight;
  if (enableTextWrapping && wrapText) {
    const headerPadding = 6;
    wrappedHeaders = headers.map((text, idx) => {
      const textStr = String(text || '');
      // Handle explicit newlines in header text (e.g., "Revision\nDate")
      if (textStr.includes('\n')) {
        return textStr.split('\n');
      }
      const maxWidth = headerCols[idx] - (headerPadding * 2);
      return wrapText(textStr, maxWidth, headerFontSize, font);
    });

    const maxHeaderLines = Math.max(...wrappedHeaders.map((header) => header.length), 1);
    headerRowHeight = Math.max(tableRowHeight, maxHeaderLines * (headerFontSize + 2) + headerPadding * 2);
  }

  // Header row
  page.drawRectangle({
    x: tableX,
    y: y - headerRowHeight,
    width: tableWidth,
    height: headerRowHeight,
    borderColor: rgb(0, 0, 0),
    borderWidth: 1,
    color: rgb(0.9, 0.9, 0.9),
  });

  // Column dividers
  let dividerX = tableX;
  headerCols.slice(0, -1).forEach((w) => {
    dividerX += w;
    page.drawLine({
      start: { x: dividerX, y: y - headerRowHeight },
      end: { x: dividerX, y },
      thickness: 1,
      color: rgb(0, 0, 0),
    });
  });

  // Header texts - with wrapping support, left-aligned
  let colX = tableX;
  if (enableTextWrapping && wrappedHeaders) {
    // Text wrapping mode for headers - left aligned
    const headerPadding = 6;
    const headerLineHeight = headerFontSize + 2;

    wrappedHeaders.forEach((lines, cellIdx) => {
      const cellWidth = headerCols[cellIdx];
      lines.forEach((line, lineIdx) => {
        const textX = colX + headerPadding;
        const totalTextHeight = lines.length * headerLineHeight;
        const startY = y - headerPadding - (headerRowHeight - totalTextHeight) / 2;
        const textY = startY - lineIdx * headerLineHeight;

        page.drawText(line, {
          x: textX,
          y: textY,
          size: headerFontSize,
          font,
          color: rgb(0, 0, 0),
        });
      });
      colX += cellWidth;
    });
  } else {
    // Left-aligned text mode for headers (no wrapping)
    headers.forEach((text, i) => {
      const textX = colX + 6; // Left padding
      const textY = y - headerRowHeight + (headerRowHeight - headerFontSize) / 2;
      page.drawText(text, {
        x: textX,
        y: textY,
        size: headerFontSize,
        font,
        color: rgb(0, 0, 0),
      });
      colX += headerCols[i];
    });
  }

  y -= headerRowHeight;

  // === TABLE ROWS ===
  for (let i = 0; i < selectedRows.length; i++) {
    const row = selectedRows[i];
    const rowData = formatRowData(row, i);
    const rowCols = headerCols;

    let dynamicRowHeight = tableRowHeight;

    // Handle text wrapping if enabled
    let wrappedCells = null;
    if (enableTextWrapping && wrapText) {
      const cellFontSize = fontSize;
      const lineHeight = cellFontSize + 2;
      const cellPadding = 6;

      wrappedCells = rowData.map((text, idx) => {
        const maxWidth = rowCols[idx] - (cellPadding * 2);
        return wrapText(String(text || '-'), maxWidth, cellFontSize, font);
      });

      const maxLines = Math.max(...wrappedCells.map((cell) => cell.length), 1);
      dynamicRowHeight = Math.max(tableRowHeight, maxLines * lineHeight + cellPadding * 2);
    }

    // Row border
    page.drawRectangle({
      x: tableX,
      y: y - dynamicRowHeight,
      width: tableWidth,
      height: dynamicRowHeight,
      borderColor: rgb(0, 0, 0),
      borderWidth: 1,
    });

    // Column dividers
    let cellDividerX = tableX;
    rowCols.slice(0, -1).forEach((w) => {
      cellDividerX += w;
      page.drawLine({
        start: { x: cellDividerX, y: y - dynamicRowHeight },
        end: { x: cellDividerX, y },
        thickness: 1,
        color: rgb(0, 0, 0),
      });
    });

    // Draw cell content
    let cellX = tableX;
    if (enableTextWrapping && wrappedCells) {
      // Text wrapping mode
      const cellFontSize = fontSize;
      const lineHeight = cellFontSize + 2;
      const cellPadding = 6;

      wrappedCells.forEach((lines, cellIdx) => {
        const cellWidth = rowCols[cellIdx];
        lines.forEach((line, lineIdx) => {
          const textX = cellX + cellPadding;
          const totalTextHeight = lines.length * lineHeight;
          const startY = y - cellPadding - (dynamicRowHeight - totalTextHeight) / 2;
          const textY = startY - lineIdx * lineHeight;

          page.drawText(line, {
            x: textX,
            y: textY,
            size: cellFontSize,
            font,
            color: rgb(0, 0, 0),
          });
        });
        cellX += cellWidth;
      });
    } else {
      // Left-aligned text mode
      const cellPadding = 6;
      rowData.forEach((text, i) => {
        const textStr = String(text || '-');
        const textX = cellX + cellPadding;
        const textY = y - dynamicRowHeight + (dynamicRowHeight - fontSize) / 2;
        page.drawText(textStr, {
          x: textX,
          y: textY,
          size: fontSize,
          font,
        });
        cellX += rowCols[i];
      });
    }

    y -= dynamicRowHeight;

    if (y < 100) {
      page = mergedPdf.addPage([595, 842]);
      y = height - 100;
    }
  }

  // === Section 2.0 ===
  y -= 40;
  page.drawText(section2Title, {
    x: 50,
    y,
    size: 14,
    font,
  });
  y -= 10;
  page.drawLine({
    start: { x: 50, y: y },
    end: { x: width - 50, y: y },
    thickness: 2,
    color: rgb(0.7, 0.7, 0.7),
  });

  // === Append Certificates (parallel fetch + fast vector merge when possible) ===
  const pdfBuffers = await prefetchCertificatePdfBuffers(
    selectedRows,
    getCertificateUrl,
    Math.max(1, fetchConcurrency),
    onProgress
  );

  for (let idx = 0; idx < selectedRows.length; idx++) {
    const pdfBytes = pdfBuffers[idx];

    if (pdfBytes == null || pdfBytes === false) {
      failCount++;
      continue;
    }

    try {
      const ok = await appendCertificatePdfToMerged(
        mergedPdf,
        pdfBytes,
        font,
        renderPdfPageAsImage
      );
      if (ok) {
        successCount++;
        onProgress?.({ phase: 'merge', index: idx, total: selectedRows.length, status: 'ok' });
      } else {
        failCount++;
      }
    } catch (error) {
      console.error(`Error processing certificate for row ${idx}:`, error);
      failCount++;
    }
  }

  // === PAGE NUMBERS ===
  const totalPages = mergedPdf.getPageCount();

  for (let i = 0; i < totalPages; i++) {
    try {
      const p = mergedPdf.getPage(i);
      const pageSize = p.getSize();
      const pageText = `Page ${i + 1} of ${totalPages}`;
      const pageFontSize = 9;
      const textW = font.widthOfTextAtSize(pageText, pageFontSize);

      // Use the actual page width instead of the summary page width
      const pageWidth = pageSize.width;
      const xPosition = (pageWidth - textW) / 2;

      p.drawText(pageText, {
        x: xPosition,
        y: 10,
        size: pageFontSize,
        font,
      });
    } catch (error) {
      // Silently continue if page number can't be added
    }
  }

  // Save merged PDF
  const mergedPdfBytes = await mergedPdf.save();

  const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return {
    successCount,
    failCount,
    totalPages: mergedPdf.getPageCount(),
  };
};

